import {AGENCY_KEYS} from "@/api/agencyApi";
import {MISC_KEYS, useCrawlingMutation} from "@/api/miscApi";
import type {CrawledBrandDataSerializer} from "@/generated/DjangoTypes";
import {useFetchUser} from "@/hooks/useFetchUser";
import {useSSE} from "@/hooks/useSSE";
import {client} from "@/httpClient";
import {queryClient} from "@/queryClient";
import {AddressSchema} from "@/store/company";
import useRegistrationStore from "@/store/registration";
import useUserStore from "@/store/user";
import {useMutation} from "@tanstack/react-query";
import {useEffect, useState} from "react";
import {useEffectOnce} from "react-use";
import {useImmer} from "use-immer";
import {useShallow} from "zustand/shallow";

export type TCustomerState = Partial<{
  address_city: string | null;
  address_postal: string | null;
  address_street: string | null;
  bio: string;
  company_name: string;
  company_overview: string | null;
  country: string | null;
  customer_pain_points: string | null;
  file: File | string;
  phonenumber: string;
  place_description: string | null;
  place_id: string | null;
  products_overview: string | null;
  session_id: string;
  website: string;
}>;

const useCustomerCreation = ({agencyId, onSuccess}: {agencyId?: number; onSuccess(companyId: number): void}) => {
  const {refetch: refetchUser} = useFetchUser();
  const country = useUserStore(state => state.user.country);
  const setUserData = useUserStore(state => state.setUserData);
  const [state, setState] = useImmer<TCustomerState>({});
  const [errors, setErrors] = useImmer<Record<string, string>>({});
  const [isCrawlingInProgress, setCrawlingInProgress] = useState(false);
  const [isDynamicAddressFocused, setDynamicAddressFocused] = useState(false);
  const [showCrawlingDisclaimer, setShowCrawlingDisclaimer] = useState(false);

  const {companyInstagramUsername: instagramError, companyTikTokUsername: tiktokError} = useRegistrationStore(
    state => state.errors,
  );
  const [insta, tiktok, interests] = useRegistrationStore(
    useShallow(state => [state.companyInstagramUsername, state.companyTikTokUsername, state.interests]),
  );
  const setRegistrationValue = useRegistrationStore(state => state.setValue);
  const resetRegistrationStore = useRegistrationStore(state => state.reset);

  const onCrawlingEnd = () => {
    setShowCrawlingDisclaimer(true);
    setCrawlingInProgress(false);
  };

  const {mutate: create, isPending: isCreating} = useMutation({
    meta: {skipSuccessToast: true},
    mutationKey: AGENCY_KEYS.mAgencySubBrandRegister(agencyId!),
    mutationFn: () => {
      const formData = new FormData();

      for (const key in state) {
        const typedKey = key as keyof typeof state;
        if (!state[typedKey]) continue;
        formData.append(typedKey, state[typedKey]!);
      }

      if (insta) {
        formData.append("username", insta);
      }

      if (tiktok) {
        formData.append("tiktok_username", tiktok);
      }

      if (interests && interests.length > 0) {
        formData.append("interests", interests.join(","));
      }

      if (!!state.file && typeof state.file !== "string") {
        formData.append("picture", state.file!);
        formData.append("picture320", state.file!);
        formData.append("picture640", state.file!);
      }

      return client.post(AGENCY_KEYS.mAgencySubBrandRegister(agencyId!)[0], formData, {
        headers: {"Content-Type": "multipart/form-data"},
      });
    },
    onSuccess: async response => {
      await Promise.all([
        queryClient.invalidateQueries({queryKey: AGENCY_KEYS.qAgency(agencyId!)}),
        queryClient.invalidateQueries({queryKey: AGENCY_KEYS.qAgencySubbrands(agencyId!)}),
      ]);
      const {data: refetchedUser} = await refetchUser();

      if (refetchedUser) {
        setUserData(refetchedUser);
      }

      setState({});
      resetRegistrationStore();
      onSuccess(response.data.id);
    },
  });

  const {mutate: crawlDomain, data: crawlingDomainData} = useCrawlingMutation({
    domain: state.website!,
    onError: () => onCrawlingEnd(),
    onSuccess: sessionId =>
      setState(draft => {
        draft.session_id = sessionId;
      }),
  });

  const {data: crawledBrandData} = useSSE<CrawledBrandDataSerializer>({
    initialState: {},
    queryKey: MISC_KEYS.qCrawlingStream(crawlingDomainData?.data?.session_id!),
    enabled: !!crawlingDomainData?.data?.session_id,
    onError: () => onCrawlingEnd(),
    onSuccess: () => onCrawlingEnd(),
  });

  useEffectOnce(() => {
    setState(draft => {
      draft.country = country ?? "Switzerland";
    });
  });

  useEffect(() => {
    if (crawledBrandData) {
      setState(draft => {
        draft.bio = crawledBrandData.company_bio ?? undefined;
        draft.company_name = crawledBrandData.company_title ?? undefined;
        draft.company_overview = crawledBrandData.company_overview ?? undefined;
        draft.customer_pain_points = crawledBrandData.customer_pain_points ?? undefined;
        draft.file = crawledBrandData.company_logo_url ?? undefined;
        draft.products_overview = crawledBrandData.products_overview ?? undefined;
      });

      setRegistrationValue("companyInstagramUsername", crawledBrandData.instagram_username ?? undefined);
      setRegistrationValue("companyTikTokUsername", crawledBrandData.tiktok_username ?? undefined);
      setRegistrationValue("interests", crawledBrandData.interests ?? undefined);
    }
  }, [crawledBrandData]);

  useEffect(() => {
    if (state.address_city === undefined && state.address_postal === undefined && state.address_street === undefined) {
      return;
    }

    setErrors({});
    const result = AddressSchema.safeParse(state);

    if (!result.success) {
      result.error.issues.map(issue => {
        setErrors(state => {
          if (state) {
            state[issue.path[0] as string] = issue.message;
          } else {
            state = {[issue.path[0]]: issue.message};
          }
        });
      });
    }
  }, [state.address_city, state.address_postal, state.address_street, state.country]);

  return {
    create,
    errors,
    isCrawledFieldEmpty: (value?: File | null | number | string) => showCrawlingDisclaimer && value === undefined,
    isCrawlingInProgress,
    isCreateDisabled:
      !state.company_name ||
      !state.country ||
      (!insta && !tiktok) ||
      !!instagramError ||
      !!tiktokError ||
      !interests?.length,
    isCreating,
    isDynamicAddressFocused,
    reset: () => {
      setState({});
      resetRegistrationStore();
    },
    setDynamicAddressFocused,
    setState,
    showCrawlingDisclaimer,
    startCrawling: () => {
      setCrawlingInProgress(true);
      setShowCrawlingDisclaimer(false);
      crawlDomain();
    },
    state,
  };
};

export type TCustomerCreation = ReturnType<typeof useCustomerCreation>;

export default useCustomerCreation;
